import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {Router} from '@angular/router';
import {TranslatePipe} from '@ngx-translate/core';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {lastValueFrom} from 'rxjs';
import {CustomerProductHistoryTimelineSummary, CustomerProductService, PartyType} from '../../../../../api';
import {ElciaCard} from '../../../../components/card/card';
import {Loader} from '../../../../components/loader/loader';
import {
  PeriodNavigatorComponent,
  PeriodRange,
  PeriodType,
} from '../../../../components/period-navigator/period-navigator';
import {QueryKey} from '../../../../constants/query-key';
import {RouteUri} from '../../../../constants/route-uri';
import {DashboardProductHistoryItem} from '../dashboard-product-history-item/dashboard-product-history-item';

@Component({
  selector: 'elcia-dashboard-product-history',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, ElciaCard, Loader, PeriodNavigatorComponent, DashboardProductHistoryItem],
  templateUrl: './dashboard-product-history.html',
  host: {class: 'block min-w-0'},
})
export class DashboardProductHistory {
  private readonly customerProductService = inject(CustomerProductService);
  private readonly router = inject(Router);

  protected readonly PeriodType = PeriodType;

  // Filled by the navigator on init: the month it opens on is the period the timeline starts with.
  private readonly period = signal<PeriodRange | null>(null);

  private readonly historyQuery = injectQuery(() => ({
    queryKey: [QueryKey.DASHBOARD_PRODUCT_HISTORY, this.period()?.startDate, this.period()?.endDate],
    queryFn: () => lastValueFrom(this.customerProductService.getCustomerProductHistoryTimeline(
      this.period()!.startDate,
      this.period()!.endDate,
    )),
    enabled: this.period() !== null,
  }));

  protected readonly history = computed<CustomerProductHistoryTimelineSummary[]>(
    () => this.historyQuery.data()?.history ?? [],
  );

  // isPending rather than isLoading: it also covers the frame before the navigator has set the period.
  protected readonly isLoading = computed(() => this.historyQuery.isPending());

  protected setPeriod(period: PeriodRange): void {
    this.period.set(period);
  }

  protected openCustomer(entry: CustomerProductHistoryTimelineSummary): void {
    // A company opens straight on its products tab; a contact has no such tab, its own file is the
    // closest match. New tab so the dashboard stays where the user left it.
    const commands = entry.customerType === PartyType.Company
      ? ['/' + RouteUri.DIRECTORY_FORM, entry.customerId, RouteUri.DIRECTORY_FORM_PRODUCTS]
      : ['/' + RouteUri.DIRECTORY_FORM, RouteUri.DIRECTORY_FORM_CONTACT, entry.customerId];

    window.open(this.router.serializeUrl(this.router.createUrlTree(commands)), '_blank');
  }
}
